var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import '../vitesse-icon';
let VitesseSelect = class VitesseSelect extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.value = '';
        this.placeholder = '';
        this.disabled = false;
        /** Command-palette mode: picking emits `change` but the trigger never holds the value,
         *  it stays on the placeholder (e.g. the ban-a-tool picker). */
        this.stateless = false;
        this.open = false;
        this.onDocClick = (e) => {
            if (this.open && !e.composedPath().includes(this))
                this.open = false;
        };
        this.onKey = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
        this.reposition = () => {
            if (this.open)
                this.position();
        };
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        this.removeRepositionListeners();
    }
    // The options panel renders in the TOP LAYER via the Popover API: the SAME
    // structural fix the modal dialog uses, so it's never clipped by an ancestor's
    // overflow (e.g. a dialog's `overflow: hidden`, which used to hide it under the
    // dialog's bottom edge). Popovers aren't anchor-positioned cross-browser yet, so
    // we place it at the trigger ourselves + flip up when there's no room below.
    updated() {
        const p = this.panelEl;
        if (!p)
            return;
        if (this.open) {
            try {
                p.showPopover();
            }
            catch {
                /* already shown */
            }
            this.position();
            window.addEventListener('scroll', this.reposition, true);
            window.addEventListener('resize', this.reposition);
        }
        else {
            try {
                p.hidePopover();
            }
            catch {
                /* already hidden */
            }
            this.removeRepositionListeners();
        }
    }
    removeRepositionListeners() {
        window.removeEventListener('scroll', this.reposition, true);
        window.removeEventListener('resize', this.reposition);
    }
    position() {
        const t = this.triggerEl;
        const p = this.panelEl;
        if (!t || !p)
            return;
        const r = t.getBoundingClientRect();
        p.style.position = 'fixed';
        p.style.margin = '0';
        p.style.inset = 'auto';
        p.style.left = `${r.left}px`;
        p.style.width = `${r.width}px`;
        const below = window.innerHeight - r.bottom;
        const need = Math.min(240, p.scrollHeight || 240) + 12;
        if (below < need && r.top > below) {
            // Not enough room below AND more room above → open upward.
            p.style.top = 'auto';
            p.style.bottom = `${window.innerHeight - r.top + 6}px`;
        }
        else {
            p.style.bottom = 'auto';
            p.style.top = `${r.bottom + 6}px`;
        }
    }
    pick(v) {
        this.open = false;
        if (!this.stateless)
            this.value = v;
        this.dispatchEvent(new CustomEvent('change', { detail: { value: v }, bubbles: true, composed: true }));
    }
    static { this.styles = css `
    :host {
      position: relative;
      display: block;
    }
    .trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      border: 1px solid var(--border-color-light);
      border-radius: var(--radius, 6px);
      background: var(--bg-primary);
      color: var(--text-primary);
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      transition: border-color 0.18s ease;
    }
    /* Fixed heights that match base-button, opted into via the size attribute. Zero vertical
       padding; the height centres the label. */
    :host([size='sm']) .trigger { height: 26px; padding: 0 10px; font-size: 12px; }
    :host([size='md']) .trigger { height: 30px; padding: 0 12px; font-size: 13px; }
    :host([size='lg']) .trigger { height: 36px; padding: 0 14px; font-size: 14px; }
    .trigger:hover,
    .trigger.open {
      border-color: var(--blue);
    }
    .value {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
    }
    .value.placeholder {
      color: var(--text-secondary);
    }
    .chev {
      color: var(--text-secondary);
      flex-shrink: 0;
      transition: transform 0.18s ease;
    }
    .chev.open {
      transform: rotate(180deg);
    }
    :host([disabled]) .trigger {
      opacity: 0.55;
      cursor: not-allowed;
      pointer-events: none;
    }
    /* Top-layer popover, positioned at the trigger in JS (position()), so no
       static position here; the UA gives it position:fixed when shown. */
    .panel {
      /* Overridable per use: a picker whose list is naturally long (specialists)
         caps lower so the menu stays a menu. */
      max-height: var(--vitesse-select-menu-max-height, 240px);
      overflow-y: auto;
      background: var(--card-bg);
      border: 1px solid var(--border-color);
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      padding: 4px 0;
      margin: 0;
      animation: in 0.15s ease-out;
    }
    /* Closed popover paints nothing (belt-and-suspenders over the UA default). */
    .panel:not(:popover-open) {
      display: none;
    }
    @keyframes in {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .item {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      box-sizing: border-box;
      padding: 8px 12px;
      border: none;
      background: transparent;
      cursor: pointer;
      font: inherit;
      font-size: 14px;
      color: var(--text-primary);
      text-align: left;
    }
    .item:hover,
    .item.highlighted {
      background: rgb(from var(--blue) r g b / 0.15);
    }
    .item.selected {
      color: var(--blue);
    }
    .item[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .item[disabled]:hover {
      background: transparent;
    }
    .item .hint {
      margin-left: auto;
      padding-left: 12px;
      font-size: 11px;
      white-space: nowrap;
      color: var(--text-tertiary, var(--text-secondary));
    }
    .item .lbl {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `; }
    currentLabel() {
        const o = this.options.find((opt) => opt.value === this.value);
        return o ? o.label : '';
    }
    render() {
        const lbl = this.currentLabel();
        return html `
      <button
        type="button"
        class="trigger ${this.open ? 'open' : ''}"
        ?disabled=${this.disabled}
        @click=${() => (this.open = !this.open)}
      >
        <span class="value ${lbl ? '' : 'placeholder'}">${lbl || this.placeholder}</span>
        <vitesse-icon class="chev ${this.open ? 'open' : ''}" name="chevron-down" size="16"></vitesse-icon>
      </button>
      <div class="panel" popover="manual">
        ${this.options.map((o) => html `<button
            type="button"
            class="item ${o.value === this.value ? 'selected' : ''}"
            ?disabled=${o.disabled}
            title=${o.title ?? o.hint ?? ''}
            @click=${() => !o.disabled && this.pick(o.value)}
          >
            <span class="lbl">${o.label}</span>
            ${o.hint ? html `<span class="hint">${o.hint}</span>` : nothing}
            ${o.value === this.value ? html `<vitesse-icon name="check" size="14"></vitesse-icon>` : nothing}
          </button>`)}
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseSelect.prototype, "options", void 0);
__decorate([
    property()
], VitesseSelect.prototype, "value", void 0);
__decorate([
    property()
], VitesseSelect.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseSelect.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true })
], VitesseSelect.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], VitesseSelect.prototype, "stateless", void 0);
__decorate([
    state()
], VitesseSelect.prototype, "open", void 0);
__decorate([
    query('.trigger')
], VitesseSelect.prototype, "triggerEl", void 0);
__decorate([
    query('.panel')
], VitesseSelect.prototype, "panelEl", void 0);
VitesseSelect = __decorate([
    customElement('vitesse-select')
], VitesseSelect);
export { VitesseSelect };
//# sourceMappingURL=vitesse-select.js.map